/**
 * EQuIP PIMs Query Hooks
 *
 * React Query hooks for PIMs (Provider Incentive Management System) data.
 * Wraps equipApi.dashboard.getPimsFacilitySummary and getPatientPims
 * with typed key factories and enabled guards.
 */

import { useQuery } from '@tanstack/react-query'
import { equipApi } from '@/services/equipApi'
import type { PIMsFacilitySummary, PIMsAuditItem } from '@/lib/equip-types'

// ---------------------------------------------------------------------------
// Key factory
// ---------------------------------------------------------------------------

export const equipPimsKeys = {
  all: () => ['equip', 'pims'] as const,
  facilitySummary: (npi: string, year: number, quarter: number) =>
    ['equip', 'pims', 'facility-summary', npi, year, quarter] as const,
  patientPims: (patientId: string, year: number, quarter: number) =>
    ['equip', 'pims', 'patient', patientId, year, quarter] as const,
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Fetches the PIMs facility summary for a provider (identified by NPI).
 * Query is disabled when npi is null/undefined/empty.
 */
export function usePimsFacilitySummary(
  npi: string | undefined,
  year: number,
  quarter: number
) {
  return useQuery<PIMsFacilitySummary[]>({
    queryKey: equipPimsKeys.facilitySummary(npi ?? '', year, quarter),
    queryFn: () => equipApi.dashboard.getPimsFacilitySummary(npi!, year, quarter),
    enabled: !!npi,
  })
}

/**
 * Fetches the PIMs audit item for a specific patient.
 * Query is disabled when patientId is null/undefined/empty.
 * Returns PIMsAuditItem | null — null when no PIMs data exists for the patient.
 */
export function usePatientPims(
  patientId: string | undefined,
  year: number,
  quarter: number
) {
  return useQuery<PIMsAuditItem | null>({
    queryKey: equipPimsKeys.patientPims(patientId ?? '', year, quarter),
    queryFn: () => equipApi.dashboard.getPatientPims(patientId!, year, quarter),
    enabled: !!patientId,
  })
}
